// src/components/HeaderBar.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Header } from "antd/es/layout/layout";
import {
  Avatar,
  Dropdown,
  Typography,
  Space,
  Badge,
  Tooltip,
  Button,
  message,
  Drawer,
  Tabs,
  Divider,
  Skeleton
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
  ClockCircleOutlined,
  UserAddOutlined,
  CalendarOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  SettingOutlined,
  MessageOutlined,
  WarningOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { logout, setActiveRole } from "../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import type { AppDispatch, RootState } from "../redux/store";
import useSignalRNotifications from '../hooks/useSignalRNotifications';
import { getUserProfileImageUrl } from "../utils/fileUrlUtils";
import {
  fetchNotifications,
  fetchUnreadCount,
  markOneAsRead,
  markAllAsReadThunk,
  prependNotification
} from "../redux/slices/notificationSlice";
import { useAppContext } from '../contexts/AppContext';
import SubscriptionBadge from './SubscriptionBadge';

const { Text, Title } = Typography;

const roleDescriptions: Record<string, string> = {
  admin: "Full system access — manage users, roles & settings.",
  recruiter: "Manage jobs, candidates & assessments.",
  employee: "Employee self-service: leave, payslips & requests.",
  candidate: "Search and apply for jobs, track applications.",
  accountant: "Pay & Benefits, payslips and tax management.",
  platform: "Platform super-admin — manage all organizations.",
};

const roleDisplayNames: Record<string, string> = {
  admin: "Admin",
  recruiter: "Recruiter",
  employee: "Employee",
  candidate: "Candidate",
  accountant: "Pay & Benefits", // Display-only label; backend role stays "accountant"
  hr: "HR", // Keep HR uppercase
  platform: "Platform Admin",
};
const capitalize = (s?: string) =>
  s ? `${s.charAt(0).toUpperCase()}${s.slice(1).toLowerCase()}` : "";

// ─── AllNotificationsModal ────────────────────────────────────────────────────
// Defined at module level to prevent remount on every HeaderBar render.

interface AllNotificationsModalProps {
  open: boolean;
  onClose: () => void;
  activeTab: string;
  onTabChange: (key: string) => void;
  modalNotifications: any[];
  modalLoading: boolean;
  hasMoreNotifications: boolean;
  unreadCount: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onMarkAsRead: (id: number) => void;
  onMarkAllAsRead: () => void;
  getFilteredModalNotifications: () => any[];
  groupNotificationsByTime: (items: any[]) => { label: string; items: any[] }[];
  getModalNotificationAvatar: (item: any) => React.ReactNode;
  getNotificationTitle: (item: any) => string;
  getTimeAgo: (dateString: string) => string;
}

const AllNotificationsModal: React.FC<AllNotificationsModalProps> = ({
  open,
  onClose,
  activeTab,
  onTabChange,
  modalNotifications,
  modalLoading,
  hasMoreNotifications,
  unreadCount,
  scrollContainerRef,
  onScroll,
  onMarkAsRead,
  onMarkAllAsRead,
  getFilteredModalNotifications,
  groupNotificationsByTime,
  getModalNotificationAvatar,
  getNotificationTitle,
  getTimeAgo,
}) => (
  <Drawer
    title={
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Space>
          <BellOutlined style={{ color: "#00BAA7", fontSize: 18 }} />
          <span style={{ color: "#2C3E50", fontSize: 16, fontWeight: 600 }}>Notification Center</span>
          {unreadCount > 0 && <Badge count={unreadCount} style={{ backgroundColor: "#00BAA7" }} />}
        </Space>
        {unreadCount > 0 && (
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={onMarkAllAsRead}
            style={{ background: "#00BAA7", borderColor: "#00BAA7", color: "#fff", fontSize: 11 }}
          >
            Mark all read
          </Button>
        )}
      </div>
    }
    placement="right"
    width={Math.min(580, window.innerWidth - 16)}
    open={open}
    onClose={onClose}
    styles={{
      header: { background: "#fff", borderBottom: "1px solid #E8ECEF", padding: "14px 20px" },
      body: { background: "#F5F7FA", padding: 0 },
      mask: { backgroundColor: "rgba(0,0,0,0.3)" },
    }}
    closeIcon={<CloseOutlined style={{ color: "#7F8C8D" }} />}
  >
    {/* Filter tabs */}
    <div style={{ background: "#fff", borderBottom: "1px solid #E8ECEF" }}>
      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        size="small"
        tabBarStyle={{ margin: 0, padding: "0 16px", background: "#fff" }}
        items={[
          { key: "all", label: <span style={{ color: activeTab === "all" ? "#00BAA7" : "#7F8C8D" }}>All ({modalNotifications.length})</span> },
          { key: "unread", label: <span style={{ color: activeTab === "unread" ? "#00BAA7" : "#7F8C8D" }}>Unread ({modalNotifications.filter(n => !n.isRead).length})</span> },
          { key: "messages", label: <span style={{ color: activeTab === "messages" ? "#00BAA7" : "#7F8C8D" }}>Applications</span> },
          { key: "alerts", label: <span style={{ color: activeTab === "alerts" ? "#00BAA7" : "#7F8C8D" }}>Alerts</span> },
        ]}
      />
    </div>

    {/* Scrollable notification list */}
    <div
      ref={scrollContainerRef}
      style={{ height: "calc(100vh - 130px)", overflowY: "auto", background: "#F5F7FA" }}
      onScroll={onScroll}
    >
      {modalLoading && modalNotifications.length === 0 ? (
        <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 5 }} /></div>
      ) : getFilteredModalNotifications().length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 300, gap: 12 }}>
          <BellOutlined style={{ fontSize: 40, color: "#C0C7CE" }} />
          <Text style={{ color: "#7F8C8D" }}>{activeTab === "unread" ? "You're all caught up!" : "No notifications found"}</Text>
        </div>
      ) : (
        <div style={{ padding: "8px 0" }}>
          {groupNotificationsByTime(getFilteredModalNotifications()).map(group => (
            <div key={group.label}>
              <div style={{ padding: "10px 20px 4px", position: "sticky", top: 0, background: "#F5F7FA", zIndex: 1 }}>
                <Text style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 1 }}>
                  {group.label}
                </Text>
              </div>
              {group.items.map((item: any, index: number) => (
                <div
                  key={`${item.notificationId}-${index}`}
                  style={{
                    padding: "14px 20px",
                    background: item.isRead ? "#fff" : "#F0FAFA",
                    borderLeft: item.isRead ? "3px solid transparent" : "3px solid #00BAA7",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onClick={() => { if (!item.isRead) onMarkAsRead(item.notificationId); }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = item.isRead ? "#F5F7FA" : "#E6F7F5"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = item.isRead ? "#fff" : "#F0FAFA"; }}
                >
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    {getModalNotificationAvatar(item)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                        <Text strong={!item.isRead} style={{ color: item.isRead ? "#7F8C8D" : "#2C3E50", fontSize: 13 }}>
                          {getNotificationTitle(item)}
                        </Text>
                        <Text style={{ color: "#9CA3AF", fontSize: 10, whiteSpace: "nowrap", marginLeft: 8 }}>
                          {getTimeAgo(item.createdAt)}
                        </Text>
                      </div>
                      <Text style={{ color: item.isRead ? "#9CA3AF" : "#4B5563", fontSize: 12, display: "block", marginBottom: 6 }}>
                        {item.message}
                      </Text>
                      {(item.type === "application" || item.type === "message") && (
                        <Space size={6}>
                          <Button
                            size="small"
                            type="primary"
                            style={{ fontSize: 11, background: "#00BAA7", borderColor: "#00BAA7", height: 22, padding: "0 8px" }}
                            onClick={e => { e.stopPropagation(); if (!item.isRead) onMarkAsRead(item.notificationId); }}
                          >
                            View Profile
                          </Button>
                          <Button
                            size="small"
                            style={{ fontSize: 11, height: 22, padding: "0 8px", borderColor: "#0071BD", color: "#0071BD" }}
                            onClick={e => e.stopPropagation()}
                          >
                            Schedule
                          </Button>
                          <Button
                            size="small"
                            danger
                            style={{ fontSize: 11, height: 22, padding: "0 8px" }}
                            onClick={e => e.stopPropagation()}
                          >
                            Reject
                          </Button>
                        </Space>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {modalLoading && modalNotifications.length > 0 && (
            <div style={{ padding: "16px 20px" }}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          )}
          {!hasMoreNotifications && modalNotifications.length > 0 && (
            <div style={{ textAlign: "center", padding: 20, borderTop: "1px solid #E8ECEF" }}>
              <Text style={{ color: "#9CA3AF", fontSize: 12 }}>All notifications loaded</Text>
            </div>
          )}
        </div>
      )}
    </div>
  </Drawer>
);

// ─────────────────────────────────────────────────────────────────────────────

interface HeaderBarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const HeaderBar: React.FC<HeaderBarProps> = ({ collapsed, onToggle }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, activeRole, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const { setupSummary, subscription, isSubscriptionActive } = useAppContext();
  const getRoleDisplayName = (role?: string) => {
    if (!role) return "";
    const key = role.trim().toLowerCase();
    return roleDisplayNames[key] || capitalize(role);
  };

  // Get notifications from Redux
  const {
    items: notifications = [], // Add default empty array
    unreadCount,
    loading,
    dropdownLoaded,
    pageNo,
    pageSize,
    totalPages,
    totalCount,
  } = useSelector((state: RootState) => state.notifications);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAllNotificationsModal, setShowAllNotificationsModal] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [modalPage, setModalPage] = useState(1);
  const [modalNotifications, setModalNotifications] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);

  const scrollContainerRef = useRef<HTMLDivElement>(null);


  useSignalRNotifications();

  // Live clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentDate(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Ensure activeRole is initialized
  useEffect(() => {
    if (user && !activeRole) {
      const savedRole = localStorage.getItem("activeRole");
      const fallback =
        savedRole ||
        (Array.isArray(user.roles) && user.roles.length
          ? user.roles[0]
          : user.role || null);
      if (fallback) {
        dispatch(setActiveRole(fallback));
      }
    }
  }, [user, activeRole, dispatch]);

  // Fetch only unread count on mount (lightweight badge count).
  // Notifications list (GetAll) is fetched on-demand when user clicks bell icon.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUnreadCount());
    }
  }, [isAuthenticated, dispatch]);

  // Load notifications for modal with virtual scrolling
  const loadModalNotifications = useCallback(async (page: number, reset: boolean = false) => {
    // Prevent multiple simultaneous calls
    if (modalLoading) return;

    setModalLoading(true);
    try {
      const result = await dispatch(fetchNotifications({ pageNo: page, pageSize: 10 })).unwrap();

      if (reset) {
        setModalNotifications(result.data ?? []);
      } else {
        setModalNotifications(prev => [...prev, ...(result.data ?? [])]);
      }

      setHasMoreNotifications(page < result.totalPages);
      setModalPage(page);
    } catch (error) {
      // Backend returned 500 or network error — fail silently, stop pagination
      console.warn("Failed to load notifications (backend error):", error);
      setHasMoreNotifications(false);
      if (reset) setModalNotifications([]);
    } finally {
      setModalLoading(false);
    }
  }, [dispatch]);

  // Load modal notifications only when drawer first opens
  const loadModalNotificationsRef = useRef(loadModalNotifications);
  loadModalNotificationsRef.current = loadModalNotifications;
  useEffect(() => {
    if (showAllNotificationsModal && modalNotifications.length === 0) {
      loadModalNotificationsRef.current(1, true);
    }

  }, [showAllNotificationsModal]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled 90% down (increased threshold)
    if (scrollPercentage > 0.9 && hasMoreNotifications && !modalLoading) {
      loadModalNotifications(modalPage + 1, false);
    }
  }, [hasMoreNotifications, modalPage, loadModalNotifications]); // Remove modalLoading



  const handleLogout = () => {
    dispatch(logout());
    localStorage.removeItem("activeRole");
    message.success("Logged out");
    navigate("/login");
  };

  const handleMarkAsRead = (notificationId: number) => {
    dispatch(markOneAsRead(notificationId)).then(() => {
      dispatch(fetchUnreadCount());
      setModalNotifications(prev =>
        prev.map(item =>
          item.notificationId === notificationId
            ? { ...item, isRead: true }
            : item
        )
      );
    });
  };
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsReadThunk()).then(() => {
      dispatch(fetchUnreadCount());
      setModalNotifications(prev =>
        prev.map(item => ({ ...item, isRead: true }))
      );
    });
  };

  const dropdownNotifications = notifications.slice(0, 3);

  const getFilteredModalNotifications = () => {
    switch (activeTab) {
      case "unread":
        return modalNotifications.filter(n => !n.isRead);
      case "messages":
        return modalNotifications.filter(n => n.type === "message" || n.type === "application");
      case "alerts":
        return modalNotifications.filter(n => n.type === "reminder" || n.type === "warning");
      default:
        return modalNotifications;
    }
  };

  const name = user?.name || "User";
  const email = (user?.email ?? "").toString();
  const displayRole = (activeRole || user?.role || "Candidate").toString();
  const displayRoleLower = displayRole.toLowerCase().trim();
  const imageUrl = user?.imageUrl ? getUserProfileImageUrl(user.imageUrl) : "/placeholder.jpg";

  const roles: string[] = React.useMemo(() => {
    if (!user) return [];
    if (Array.isArray(user.roles) && user.roles.length) return user.roles;
    if (typeof user.role === "string" && user.role.includes(",")) {
      return user.role.split(",").map((r) => r.trim()).filter(Boolean);
    }
    if (typeof user.role === "string" && user.role.trim())
      return [user.role.trim()];
    return [];
  }, [user]);

  // Admin is a *held* role, independent of the currently active role — an admin
  // browsing as HR still needs one-click access to finish platform setup.
  const hasAdminRole = roles.some((r) => r.toLowerCase().trim() === "admin");
  const setupIncomplete = !!setupSummary && !setupSummary.isFullyOperational;
  const showSetupButton = hasAdminRole && setupIncomplete;

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith("role:")) {
      const raw = key.replace("role:", "");
      const roleToSet = raw.trim();
      if (!roleToSet) return;
      if (roleToSet.toLowerCase() === displayRoleLower) {
        message.info(`${capitalize(roleToSet)} is already active`);
        return;
      }
      dispatch(setActiveRole(roleToSet));
      localStorage.setItem("activeRole", roleToSet);
      navigate(`/v1/${roleToSet.toLowerCase()}/dashboard`);
      message.success(`Switched to ${capitalize(roleToSet)} role`);
      return;
    }

    switch (key) {
      case "profile":
        navigate("/v1/profile");
        break;
      case "edit-profile":
        navigate("/v1/edit-profile");
        break;
      case "logout":
        handleLogout();
        break;
    }
  };

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case "leave":
        return <ClockCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />;
      case "reminder":
        return <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 16 }} />;
      case "payroll":
        return <DollarOutlined style={{ color: "#00BAA7", fontSize: 16 }} />;
      case "application":
        return <UserAddOutlined style={{ color: "#722ed1", fontSize: 16 }} />;
      case "meeting":
        return <CalendarOutlined style={{ color: "#13c2c2", fontSize: 16 }} />;
      case "message":
        return <MessageOutlined style={{ color: "#00BAA7", fontSize: 16 }} />;
      case "warning":
        return <WarningOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />;
      default:
        return <BellOutlined style={{ color: "#00BAA7", fontSize: 16 }} />;
    }
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "JUST NOW";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} MIN AGO`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} HOURS AGO`;
    return `${Math.floor(diffInSeconds / 86400)} DAYS AGO`;
  };

  const getNotificationTitle = (item: any) => {
    return item.userName || item.employeeName || "System Notification";
  };
  const getNotificationAvatar = (item: any) => {
    if (item.userImageUrl) {
      return (
        <Avatar
          size={40}
          src={getUserProfileImageUrl(item.userImageUrl)}
          style={{
            border: item.isRead ? "2px solid #E8ECEF" : "2px solid #00BAA7",
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 20,
          backgroundColor: item.isRead ? "#F5F7FA" : "#E6F7F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: item.isRead ? "2px solid #E8ECEF" : "2px solid #00BAA7",
        }}
      >
        {getNotificationIcon(item.type)}
      </div>
    );
  };

  const getModalNotificationAvatar = (item: any) => {
    if (item.userImageUrl) {
      return (
        <Avatar
          size={52}
          src={getUserProfileImageUrl(item.userImageUrl)}
          style={{
            border: item.isRead ? "2px solid #E8ECEF" : "2px solid #00BAA7",
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 26,
          backgroundColor: item.isRead ? "#F5F7FA" : "#E6F7F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: item.isRead ? "2px solid #E8ECEF" : "2px solid #00BAA7",
        }}
      >
        {getNotificationIcon(item.type)}
      </div>
    );
  };

  const notificationMenu = (
    <div
      style={{
        width: Math.min(400, window.innerWidth - 24),
        backgroundColor: "#fff",
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        overflow: "hidden",
        border: "1px solid #E8ECEF",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#fff",
          padding: "16px 20px",
          borderBottom: "1px solid #E8ECEF",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Text strong style={{ color: "#2C3E50", fontSize: 16 }}>
              Notifications
            </Text>
            {unreadCount > 0 && (
              <Badge
                count={`${unreadCount} New`}
                style={{
                  backgroundColor: "#00BAA7",
                  color: "white",
                  marginLeft: 12,
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAllAsRead();
              }}
              style={{
                color: "#00BAA7",
                fontSize: 12,
                padding: "2px 8px",
                height: "auto",
                border: "1px solid #00BAA7",
                borderRadius: 4,
              }}
            >
              Mark all read
            </Button>
          )}
        </div>
      </div>

      <div style={{ maxHeight: 400, overflowY: "auto", backgroundColor: "#fff" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : dropdownNotifications.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <BellOutlined style={{ fontSize: 32, color: "#C0C7CE", marginBottom: 8 }} />
            <div style={{ color: "#7F8C8D" }}>No notifications</div>
          </div>
        ) : (
          dropdownNotifications.map((item, index) => (
            <div key={item.notificationId}>
              <div
                style={{
                  padding: "16px 20px",
                  cursor: "pointer",
                  backgroundColor: item.isRead ? "#fff" : "#F0FAFA",
                  borderLeft: item.isRead ? "none" : "4px solid #00BAA7",
                  transition: "all 0.2s ease",
                  position: "relative",
                }}
                onClick={() => {
                  if (!item.isRead) handleMarkAsRead(item.notificationId);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = item.isRead ? "#F5F7FA" : "#E6F7F5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = item.isRead ? "#fff" : "#F0FAFA";
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  {getNotificationAvatar(item)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                      <Text
                        strong={!item.isRead}
                        style={{
                          color: item.isRead ? "#7F8C8D" : "#2C3E50",
                          fontSize: 14,
                          lineHeight: "20px",
                        }}
                      >
                        {getNotificationTitle(item)}
                      </Text>
                      {!item.isRead && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#00BAA7",
                            flexShrink: 0,
                            marginLeft: 8,
                            marginTop: 6,
                          }}
                        />
                      )}
                    </div>
                    <Text
                      style={{
                        color: item.isRead ? "#9CA3AF" : "#4B5563",
                        fontSize: 13,
                        lineHeight: "18px",
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      {item.message.length > 80 ? `${item.message.substring(0, 80)}...` : item.message}
                    </Text>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Text
                        style={{
                          color: "#9CA3AF",
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {getTimeAgo(item.createdAt)}
                      </Text>
                      {!item.isRead && (
                        <Text style={{ color: "#00BAA7", fontSize: 11, fontStyle: "italic", opacity: 0.8 }}>
                          Click to read
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {index < dropdownNotifications.length - 1 && (
                <Divider style={{ margin: 0, backgroundColor: "#E8ECEF" }} />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "12px 20px",
          backgroundColor: "#F5F7FA",
          borderTop: "1px solid #E8ECEF",
        }}
      >
        <Button
          type="primary"
          block
          onClick={() => { setNotifDropdownOpen(false); setShowAllNotificationsModal(true); }}
          style={{
            background: "#00BAA7",
            border: "none",
            borderRadius: 6,
            height: 36,
            fontWeight: 600,
          }}
        >
          View All Notifications
        </Button>
      </div>
    </div>
  );

  const userMenu = (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.05)',
        minWidth: 190,
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
      }}
    >
      {/* User Info Header */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar
          size={32}
          src={imageUrl}
          icon={<UserOutlined />}
          style={{ border: '2px solid #E8ECEF', flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#2C3E50', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
          <div style={{ fontSize: 11, color: '#00BAA7', fontWeight: 500 }}>{getRoleDisplayName(displayRole)}</div>
        </div>
      </div>

      {/* Switch Role */}
      {roles.length > 1 && (
        <div style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ padding: '3px 14px 4px', fontSize: 10, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Switch Role
          </div>
          {roles.map((r) => {
            const isActive = r.toLowerCase() === displayRoleLower;
            return (
              <div
                key={`role:${r}`}
                onClick={() => handleUserMenuClick({ key: `role:${r}` })}
                style={{
                  padding: '5px 14px',
                  fontSize: 13,
                  color: isActive ? '#00BAA7' : '#374151',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background 0.15s ease',
                  background: isActive ? 'rgba(0,186,167,0.06)' : 'transparent',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#F9FAFB'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? 'rgba(0,186,167,0.06)' : 'transparent'; }}
              >
                <span>{getRoleDisplayName(r)}</span>
                {isActive && (
                  <span style={{ fontSize: 10, background: '#00BAA7', color: '#fff', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>Active</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Profile Links */}
      <div style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
        {[
          { key: 'profile', label: 'View Profile', icon: <UserOutlined /> },
          { key: 'edit-profile', label: 'Edit Profile', icon: <SettingOutlined /> },
        ].map((item) => (
          <div
            key={item.key}
            onClick={() => handleUserMenuClick({ key: item.key })}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              color: '#374151',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ color: '#9CA3AF', fontSize: 13 }}>{item.icon}</span>
            {item.label}
          </div>
        ))}
      </div>

      {/* Logout */}
      <div style={{ padding: '4px 0' }}>
        <div
          onClick={() => handleUserMenuClick({ key: 'logout' })}
          style={{
            padding: '6px 14px',
            fontSize: 13,
            color: '#EF4444',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <LogoutOutlined style={{ fontSize: 13 }} />
          Logout
        </div>
      </div>
    </div>
  );

  const closeDrawer = () => {
    setShowAllNotificationsModal(false);
    setModalPage(1);
    setModalNotifications([]);
    setActiveTab("all");
    setHasMoreNotifications(true);
    setModalLoading(false);
  };

  const groupNotificationsByTime = (items: any[]) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart.getTime() - 86400000);
    const weekStart = new Date(todayStart.getTime() - 6 * 86400000);
    const groups: { label: string; items: any[] }[] = [
      { label: 'Today', items: [] },
      { label: 'Yesterday', items: [] },
      { label: 'This Week', items: [] },
      { label: 'Earlier', items: [] },
    ];
    items.forEach(item => {
      const d = new Date(item.createdAt);
      if (d >= todayStart) groups[0].items.push(item);
      else if (d >= yesterdayStart) groups[1].items.push(item);
      else if (d >= weekStart) groups[2].items.push(item);
      else groups[3].items.push(item);
    });
    return groups.filter(g => g.items.length > 0);
  };



  const [viewportWidth, setViewportWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Granular breakpoints for all device sizes
  const isTinyPhone = viewportWidth <= 375;    // iPhone SE, small Android
  const isPhone = viewportWidth <= 480;         // Standard phones
  const isMobileView = viewportWidth <= 640;    // Large phones
  const isTablet = viewportWidth <= 768;        // Tablet portrait
  const isNarrowView = viewportWidth <= 900;    // Tablet landscape / 150% zoom

  // Compute header padding based on width
  const headerPadding = (() => {
    if (isTinyPhone) return '0 6px';
    if (isPhone) return '0 8px';
    if (isMobileView) return '0 10px';
    if (isTablet) return '0 12px';
    if (isNarrowView) return '0 14px';
    return '0 20px';
  })();

  // Header height: taller on desktop (60px), compact on mobile (52px), tiny (48px)
  const headerHeight = isTinyPhone ? 48 : isPhone ? 50 : isMobileView ? 52 : 60;

  return (
    <>
      <Header
        style={{
          background: "#FFFFFF",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: headerPadding,
          height: headerHeight,
          lineHeight: `${headerHeight}px`,
          borderBottom: "1px solid #E8ECEF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          position: "sticky",
          top: 0,
          zIndex: 100,
          minWidth: 0,
          width: '100%',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {/* Left: Toggle + Org name + Subscription badge */}
        <div style={{ display: "flex", alignItems: "center", gap: isTinyPhone ? 6 : 12, minWidth: 0, flex: 1, overflow: 'hidden' }}>
          {/* Mobile Menu Toggle */}
          <Button
            onClick={onToggle}
            style={{
              width: isTinyPhone ? 28 : isPhone ? 30 : 36,
              height: isTinyPhone ? 28 : isPhone ? 30 : 36,
              borderRadius: 8,
              border: "1px solid #E8ECEF",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              color: "#2C3E50",
              fontSize: isTinyPhone ? 13 : 16,
              flexShrink: 0,
            }}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </Button>

          {/* Organization name — hidden on very small screens */}
          {!isPhone && (
            <Text
              style={{
                fontSize: isMobileView ? 12 : 13,
                fontWeight: 600,
                color: '#2C3E50',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobileView ? 90 : isTablet ? 140 : isNarrowView ? 180 : 220,
              }}
            >
              ResourcAI
            </Text>
          )}

          {/* Subscription badge — hidden on tiny/phone screens */}
          {!isMobileView && subscription && (
            <SubscriptionBadge compact={isTablet} />
          )}
        </div>

        {/* Right: Icons + Profile */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: isTinyPhone ? 0 : isPhone ? 2 : isMobileView ? 4 : isNarrowView ? 6 : 8,
          flexShrink: 0,
        }}>
          {/* Complete Setup — shown to anyone holding the Admin role (even when
              the active role is HR/Accountant/etc.) while setup is incomplete. */}
          {showSetupButton && (
            <Tooltip title="Finish platform setup">
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={() => navigate('/v1/admin/setup-center')}
                style={{
                  background: '#00BAA7',
                  borderColor: '#00BAA7',
                  borderRadius: 8,
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0,186,167,0.25)',
                  paddingInline: isPhone ? 8 : 14,
                }}
              >
                {!isMobileView && 'Complete Setup'}
              </Button>
            </Tooltip>
          )}

          {/* Notifications */}
          <Dropdown
            popupRender={() => notificationMenu}
            trigger={["click"]}
            arrow={false}
            placement="bottomRight"
            open={notifDropdownOpen}
            onOpenChange={(open) => {
              setNotifDropdownOpen(open);
              // Fetch notifications only when dropdown opens & not already loaded
              if (open && !dropdownLoaded) {
                dispatch(fetchNotifications({ pageNo: 1, pageSize: 10 }));
              }
            }}
          >
            <Tooltip title="Notifications">
              <div
                style={{
                  width: isTinyPhone ? 28 : isPhone ? 30 : 40,
                  height: isTinyPhone ? 28 : isPhone ? 30 : 40,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  color: "#7F8C8D",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F5F7FA"; e.currentTarget.style.color = "#00BAA7"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#7F8C8D"; }}
              >
                <Badge
                  count={unreadCount}
                  offset={[-2, 2]}
                  size="small"
                  style={{ backgroundColor: "#00BAA7", fontSize: 10, fontWeight: 600 }}
                  showZero={false}
                >
                  <BellOutlined style={{ fontSize: isTinyPhone ? 14 : isPhone ? 15 : 18, color: "inherit" }} />
                </Badge>
              </div>
            </Tooltip>
          </Dropdown>

          {/* Divider — hidden on tiny phones */}
          {!isTinyPhone && (
            <div style={{ width: 1, height: isPhone ? 24 : 32, background: "#E8ECEF", margin: isPhone ? "0 4px" : "0 8px" }} />
          )}

          {/* User Info + Avatar */}
          <Dropdown popupRender={() => userMenu} trigger={["click"]} placement="bottomRight" arrow={{ pointAtCenter: true }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: isNarrowView ? 4 : 10,
              cursor: "pointer",
              padding: isTinyPhone ? "2px 4px" : "4px 6px",
              borderRadius: 8,
              transition: "background 0.2s"
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "#F5F7FA"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              {/* Show name/role only on desktop- and laptop-sized screens */}
              {!isNarrowView && (
                <div style={{ textAlign: "right", lineHeight: "18px" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#2C3E50", whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ fontSize: 11, color: "#00BAA7", fontWeight: 500 }}>{getRoleDisplayName(displayRole)}</div>
                </div>
              )}
              <Avatar
                size={isTinyPhone ? 26 : isPhone ? 28 : isNarrowView ? 32 : 36}
                src={imageUrl}
                icon={<UserOutlined />}
                style={{ border: "2px solid #E8ECEF", flexShrink: 0 }}
              />
            </div>
          </Dropdown>
        </div>
      </Header>

      <AllNotificationsModal
        open={showAllNotificationsModal}
        onClose={closeDrawer}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        modalNotifications={modalNotifications}
        modalLoading={modalLoading}
        hasMoreNotifications={hasMoreNotifications}
        unreadCount={unreadCount}
        scrollContainerRef={scrollContainerRef}
        onScroll={handleScroll}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        getFilteredModalNotifications={getFilteredModalNotifications}
        groupNotificationsByTime={groupNotificationsByTime}
        getModalNotificationAvatar={getModalNotificationAvatar}
        getNotificationTitle={getNotificationTitle}
        getTimeAgo={getTimeAgo}
      />
    </>
  );
};

export default HeaderBar;